import { Link } from "wouter";
import { SEO } from "@/components/SEO";
import { BeforeAfterSlider } from "@/components/ui/before-after-slider";
import { PageHero } from "@/components/ui/page-hero";
import { beforeAfterSets, heroImages } from "@/lib/image-map";
import { landscapingChildren, serviceAreaLinks } from "@/lib/site-navigation";
import { serviceSchema } from "@/lib/organization";

const description =
  "Landscaping services in Norfolk County from TLC Associates LLC, with renovation, garden planting, and stonework for local properties.";

const landscapingMediaByPath: Record<string, number> = {
  "/landscape-renovation": 4,
  "/garden-and-planting": 5,
  "/stonework": 3,
};

export default function Landscaping() {
  return (
    <>
      <SEO
        title="Landscaping Services | TLC Associates LLC"
        description={description}
        path="/landscaping"
        schema={serviceSchema("/landscaping", "Landscaping", "Landscaping")}
      />
      <PageHero
        imageSrc={heroImages["/landscape-renovation"]}
        eyebrow="Landscaper Serving Norfolk County, Massachusetts"
        headline="Landscapes Built Around Your Property"
        description="TLC Associates LLC brings renovation, gardens, planting, and stonework together in one landscaping program."
      />
      <section className="bg-[#f7f3ed] py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <p className="mb-12 text-xl leading-relaxed text-gray-700">
            TLC Associates LLC uses the existing renovation, garden, planting, and
            stonework services to build practical landscapes around the property.
          </p>
          <h2 className="mb-8 text-3xl text-[#1a3a1a]">Landscaping Services</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {landscapingChildren.map((service) => {
              const imageSet = beforeAfterSets[landscapingMediaByPath[service.path]];

              return (
                <article key={service.path} className="overflow-hidden rounded-xl bg-white shadow-sm">
                  <div className="h-56 overflow-hidden">
                    <BeforeAfterSlider
                      beforeImage={imageSet.before.large}
                      afterImage={imageSet.after.large}
                      beforeImageSmall={imageSet.before.small}
                      afterImageSmall={imageSet.after.small}
                      altText={imageSet.alt}
                      compact
                      narrowDesktop
                      narrowDesktopColumns={3}
                      sameImageVariants
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="mb-3 text-2xl text-[#1a3a1a]">
                      <Link href={service.path} className="hover:text-[#2a7a2a] hover:underline">
                        {service.name}
                      </Link>
                    </h3>
                    <p className="leading-relaxed text-gray-700">{service.summary}</p>
                  </div>
                </article>
              );
            })}
          </div>
          <h2 className="mb-8 mt-16 text-3xl text-[#1a3a1a]">
            Landscaping Service Areas in Norfolk County
          </h2>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {serviceAreaLinks.map((area) => (
              <li key={area.path}>
                <h3 className="text-xl">
                  <Link className="text-[#2a7a2a] hover:underline" href={area.path}>
                    {area.name}
                  </Link>
                </h3>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}